import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  Clapperboard,
  Link2,
  Pencil,
  UserCog,
  UserMinus,
  UserPlus,
  UserRoundPlus,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Field, FieldGroup } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ADMIN_BASE_URL, adminHref } from '@/config/config'
import { posthogPersonUrl } from '@/lib/posthogLinks'
import { EmailVerificationBadge } from '../../../../components/EmailVerificationBadge'
import { SignInTokenDialog } from '../../../../components/SignInTokenDialog'
import { StatusPill } from '../../../../components/StatusPill'
import { CacheKeys } from '../../../../lib/cache'
import {
  isOrgAdmin,
  type Org,
  type OrgMember,
  OrgRole,
  OrgUserStatus,
  toggleDemoMode,
  updateOrgSalesOwner,
  useCurrentUser,
  useStaffUsers,
} from '../../../../models/user'
import { AddOrgMemberDialog } from './AddOrgMemberDialog'
import { DeactivateUserDialog } from './DeactivateUserDialog'
import { EditOrgMemberDialog } from './EditOrgMemberDialog'
import { InviteUserDialog } from './InviteUserDialog'
import { ManageUserOrgsDialog } from './ManageUserOrgsDialog'
import { UserDetailsDialog } from './UserDetailsDialog'

const roleLabels: Record<string, string> = {
  BUSINESS_OWNER: 'Business Owner',
  ADMIN: 'Admin',
  USER: 'User',
}

function formatRole(role: string): string {
  return roleLabels[role] ?? role
}

const roleRank: Record<string, number> = {
  [OrgRole.BUSINESS_OWNER]: 0,
  [OrgRole.ADMIN]: 1,
  [OrgRole.USER]: 2,
}

/** Owners and admins first, deactivated last — the order staff scan the roster in. */
function sortMembers(members: OrgMember[]): OrgMember[] {
  return [...members].sort((a, b) => {
    const deactivated =
      Number(a.orgUser?.status === OrgUserStatus.DEACTIVATED) -
      Number(b.orgUser?.status === OrgUserStatus.DEACTIVATED)
    if (deactivated !== 0) return deactivated
    const rank = (roleRank[a.orgUser?.role ?? ''] ?? 99) - (roleRank[b.orgUser?.role ?? ''] ?? 99)
    if (rank !== 0) return rank
    return `${a.firstName} ${a.lastName}`.trim().localeCompare(`${b.firstName} ${b.lastName}`.trim())
  })
}

export function OrganisationTab({
  org,
  header,
  hideUsers = false,
  showSalesOwner = false,
}: {
  org: Org
  header?: React.ReactNode
  hideUsers?: boolean
  showSalesOwner?: boolean
}) {
  const [deactivateTarget, setDeactivateTarget] = useState<OrgMember | null>(null)
  const { user } = useCurrentUser({ enabled: true })
  const queryClient = useQueryClient()
  const demoModeMutation = useMutation({
    mutationFn: (enabled: boolean) => toggleDemoMode(enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CacheKeys.CURRENT_USER] }),
  })
  const { staffUsers } = useStaffUsers()
  const salesOwnerMutation = useMutation({
    mutationFn: (salesOwnerId: string | null) => updateOrgSalesOwner(org.id, salesOwnerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, org.id] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS] })
    },
  })

  function canDeactivate(member: OrgMember): boolean {
    if (!user) return false
    if (!isOrgAdmin(user)) return false
    // Can't deactivate yourself
    if (member.id === user.id) return false
    if (member.orgUser?.status === OrgUserStatus.DEACTIVATED) return false
    // Only business owners can deactivate other business owners
    if (member.orgUser?.role === OrgRole.BUSINESS_OWNER && user.orgUser.role !== OrgRole.BUSINESS_OWNER)
      return false
    return true
  }

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader>
          {header}
          <CardTitle className="flex items-center gap-2">
            Organization <StatusPill status={org.status || ''} />
          </CardTitle>
          <CardDescription>Your organization details.</CardDescription>
        </CardHeader>
        <CardContent>
          <FieldGroup className="max-w-sm">
            <Field>
              <Label>Name</Label>
              <Input value={org.name || ''} disabled />
            </Field>
          </FieldGroup>

          {showSalesOwner && (
            <>
              <div className="mt-6 flex max-w-sm flex-col gap-2">
                <Label htmlFor="sales-owner">Sales owner</Label>
                <Select
                  value={org.salesOwner?.id ?? 'unassigned'}
                  onValueChange={(value) => salesOwnerMutation.mutate(value === 'unassigned' ? null : value)}
                >
                  <SelectTrigger id="sales-owner">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {[...staffUsers]
                      .sort((a, b) =>
                        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
                      )
                      .map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.firstName || u.lastName ? `${u.firstName} ${u.lastName}`.trim() : u.email}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <Checkbox
                  id="demo-mode"
                  checked={org.demoMode}
                  onCheckedChange={(checked) => demoModeMutation.mutate(!!checked)}
                />
                <Label htmlFor="demo-mode" className="cursor-pointer">
                  Demo mode
                  <span className="text-muted-foreground ml-1 text-sm font-normal">
                    Replace real carrier names with fictional ones
                  </span>
                </Label>
              </div>
            </>
          )}

          {!hideUsers && (
            <>
              <div className="mt-6 mb-3 flex items-center justify-between">
                <CardTitle>Members of your organization</CardTitle>
                <InviteUserDialog>
                  <Button size="sm">
                    <UserPlus className="size-4" />
                    Invite User
                  </Button>
                </InviteUserDialog>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-semibold">Name</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Role</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {org.users.length === 0 && org.pendingInvitations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="pt-5 text-center">
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                  {org.users.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">
                        {member.firstName} {member.lastName}
                      </TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{formatRole(member.orgUser?.role || '')}</TableCell>
                      <TableCell>
                        <StatusPill status={member.orgUser?.status || ''} />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {canDeactivate(member) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8"
                              onClick={() => setDeactivateTarget(member)}
                            >
                              <UserMinus className="size-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {org.pendingInvitations.map((invitation) => (
                    <TableRow key={invitation.id} className="opacity-60">
                      <TableCell className="font-medium">
                        <span className="text-muted-foreground italic">Pending</span>
                      </TableCell>
                      <TableCell>{invitation.email}</TableCell>
                      <TableCell>{formatRole(invitation.role)}</TableCell>
                      <TableCell>
                        <StatusPill status="INVITED" />
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-gray-400">Invited</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      {deactivateTarget && (
        <DeactivateUserDialog
          member={deactivateTarget}
          open={!!deactivateTarget}
          onOpenChange={(open) => {
            if (!open) setDeactivateTarget(null)
          }}
        />
      )}
    </div>
  )
}

export function UsersTab({ org, showImpersonate = false }: { org: Org; showImpersonate?: boolean }) {
  const [signInTokenTarget, setSignInTokenTarget] = useState<OrgMember | null>(null)
  const [detailsTarget, setDetailsTarget] = useState<OrgMember | null>(null)
  const [orgAccessTarget, setOrgAccessTarget] = useState<OrgMember | null>(null)
  const [editTarget, setEditTarget] = useState<OrgMember | null>(null)

  const columnCount = 4 + (showImpersonate ? 1 : 0)
  const members = useMemo(() => sortMembers(org.users), [org.users])

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader>
          <CardTitle>Users{members.length > 0 && ` (${members.length})`}</CardTitle>
          <CardDescription>Members of this organization.</CardDescription>
          <CardAction>
            <AddOrgMemberDialog orgId={org.id} orgName={org.name}>
              <Button variant="outline" size="sm">
                <UserRoundPlus />
                Add user
              </Button>
            </AddOrgMemberDialog>
          </CardAction>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Phone</TableHead>
                <TableHead className="font-semibold">Role</TableHead>
                {showImpersonate && <TableHead className="font-semibold">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} className="pt-5 text-center">
                    No users found
                  </TableCell>
                </TableRow>
              )}
              {members.map((member) => (
                <TableRow key={member.id} className="cursor-pointer" onClick={() => setDetailsTarget(member)}>
                  <TableCell className="font-medium">
                    {member.firstName} {member.lastName}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {member.email}
                      <EmailVerificationBadge status={member.emailVerification} />
                    </div>
                  </TableCell>
                  <TableCell>{member.mobile || '—'}</TableCell>
                  <TableCell>{formatRole(member.orgUser?.role || '')}</TableCell>
                  {showImpersonate && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="box-shadow-none bg-surface size-8 rounded-md"
                              onClick={() => setEditTarget(member)}
                            >
                              <Pencil className="size-4" />
                              <span className="sr-only">Edit user</span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit user</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="box-shadow-none bg-surface size-8 rounded-md"
                              onClick={() => setSignInTokenTarget(member)}
                            >
                              <Link2 className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Create login URL</TooltipContent>
                        </Tooltip>
                        {posthogPersonUrl(member.customUserId) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon"
                                className="box-shadow-none bg-surface size-8 rounded-md"
                                asChild
                              >
                                <a
                                  href={posthogPersonUrl(member.customUserId) ?? undefined}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <Clapperboard className="size-4" />
                                </a>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>View PostHog session replays</TooltipContent>
                          </Tooltip>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="box-shadow-none bg-surface size-8 rounded-md"
                              asChild
                            >
                              <a
                                href={adminHref(`${ADMIN_BASE_URL}/insure/insureuser/${member.id}/change/`)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <UserCog className="size-4" />
                              </a>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit in Django admin</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="box-shadow-none bg-surface size-8 rounded-md"
                              onClick={() => setOrgAccessTarget(member)}
                            >
                              <Building2 className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Manage organization access</TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {signInTokenTarget && (
        <SignInTokenDialog
          member={signInTokenTarget}
          open={!!signInTokenTarget}
          onOpenChange={(open) => {
            if (!open) setSignInTokenTarget(null)
          }}
        />
      )}

      {detailsTarget && (
        <UserDetailsDialog
          member={detailsTarget}
          open={!!detailsTarget}
          onOpenChange={(open) => {
            if (!open) setDetailsTarget(null)
          }}
        />
      )}

      {orgAccessTarget && (
        <ManageUserOrgsDialog
          member={orgAccessTarget}
          open={!!orgAccessTarget}
          onOpenChange={(open) => {
            if (!open) setOrgAccessTarget(null)
          }}
        />
      )}

      {editTarget && (
        <EditOrgMemberDialog
          orgId={org.id}
          member={editTarget}
          open={!!editTarget}
          onOpenChange={(open) => {
            if (!open) setEditTarget(null)
          }}
        />
      )}
    </div>
  )
}
